import { Link, useParams, useNavigate } from "react-router-dom";
import { SituationTree } from "@/features/ops/components/SituationTree";
import { useEffect, useState, useRef, useCallback } from "react";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";
import { useAuth } from "@/lib/auth-context";
import { normalizeSignal } from "@/lib/normalize";
import type { Signal } from "@/types/api";
import { SEVERITY_COLOR } from "@/types/api";
import { ruleTag, signalAge } from "@/lib/normalize";

interface LivePriceData {
  ticker: string;
  currency: string | null;
  eventPrice: number | null;
  snaps: Array<{ t: number; p: number }>;
  tracking: boolean;
  changePct: number | null;
}

function useApiBase() {
  const { status } = useAuth();
  const authed = status === "active";
  // Public base rides /api/public/* — the tunnel only forwards enumerated prefixes (/api/*).
  return { authed, base: authed ? `${config.restUrl}/api` : `${config.restUrl}/api/public` };
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const token = await getIdToken();
    if (token) return { Authorization: `Bearer ${token}` };
  } catch { /* public access */ }
  return {};
}

export function SignalDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const [signal, setSignal] = useState<Signal | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error" | "not-public">("loading");
  const { base } = useApiBase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const r = await fetch(`${base}/signals/${id}`, { headers });
        if (r.status === 403) { if (!cancelled) setState("not-public"); return; }
        if (!r.ok) throw new Error(String(r.status));
        const raw = await r.json();
        if (!cancelled) {
          setSignal(normalizeSignal(raw));
          setState("ok");
        }
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [id, base]);

  return (
    <div id="viewport" className="vp-fluid">
      <div className="event-stage">
        <div className="event-page">
          <EventHeader />
          {state === "loading" && <div className="auth-copy" style={{ padding: "40px 0", textAlign: "center" }}>Retrieving event record…</div>}
          {state === "not-public" && (
            <div className="auth-copy" style={{ padding: "40px 24px", textAlign: "center" }}>
              This event is not yet public. Events become publicly accessible 1 minute after detection.{" "}
              <Link to="/signin" style={{ color: "var(--green)" }}>Sign in</Link> for instant access.
            </div>
          )}
          {state === "error" && (
            <div className="auth-copy" style={{ padding: "40px 24px", textAlign: "center" }}>
              Could not load event <b>{id}</b>. It may have expired from the live feed.{" "}
              <Link to="/signin" style={{ color: "var(--green)" }}>Sign in</Link> to access all records.
            </div>
          )}
          {state === "ok" && signal && <EventContent signal={signal} />}
          <EventFooter />
        </div>
        <div className="tex grain" />
        <div className="tex scan" />
        <div className="tex vign" />
      </div>
    </div>
  );
}

function EventHeader() {
  return (
    <div className="event-head">
      <Link to="/" className="auth-brand" style={{ textDecoration: "none" }}>
        <div className="sigil" />
        <div>
          <div className="name">EARLYWIRE</div>
          <div className="sub">OSINT Signal Intelligence</div>
        </div>
      </Link>
      <div style={{ display: "flex", gap: 8 }}>
        <Link to="/" className="mbtn" style={{ fontSize: 10, color: "var(--green)", borderColor: "var(--green-d)" }}>GET ACCESS</Link>
      </div>
    </div>
  );
}

function EventFooter() {
  return (
    <div className="event-foot">
      <span>© {new Date().getUTCFullYear()} EARLYWIRE · OSINT-DRIVEN MARKET INTELLIGENCE</span>
      <span style={{ color: "var(--green)" }}>EARLYWIRE.IO</span>
    </div>
  );
}

function EventContent({ signal: s }: { signal: Signal }) {
  const { base } = useApiBase();

  return (
    <div className="event-body">
      {/* Hero */}
      <div className="event-hero">
        <div className="event-sev-bar" style={{ background: SEVERITY_COLOR[s.severity] }} />
        <div className="event-hero-inner">
          <div className="d-meta" style={{ marginBottom: 6 }}>
            <span className="sevtag" style={{ color: SEVERITY_COLOR[s.severity], borderColor: SEVERITY_COLOR[s.severity] }}>
              {s.severity}
            </span>
            {s.status === "live" && <span className="lifecycle-badge live">LIVE</span>}
            {s.status === "resolved" && <span className="lifecycle-badge resolved">RESOLVED</span>}
            {s.status === "live" && !s.statusReliable && <span className="lifecycle-badge unknown">STATUS UNKNOWN</span>}
            <span className="d-id">{ruleTag(s.rule)} · {signalAge(s.createdAt, s.updatedAt)}</span>
          </div>
          <h1 className="event-title">{s.title}</h1>
          {s.tickers.length > 0 && (
            <div className="tickers" style={{ marginTop: 10 }}>
              {s.tickers.map((t) => <span className="tk" key={t}>{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {/* Two-column: live price + map */}
      <div className="event-grid">
        {s.tickers.length > 0 && (
          <div className="event-card">
            <div className="h">Live Price Impact</div>
            <LivePricePanel signalId={s.id} ticker={s.tickers[0]} eventMs={s.createdAt} base={base} />
          </div>
        )}
        <div className="event-card">
          <div className="h">Location</div>
          <MiniMap lat={s.lat} lon={s.lon} title={s.title} />
          <div className="mono-xs" style={{ marginTop: 6 }}>{s.lat.toFixed(3)}°, {s.lon.toFixed(3)}° · {s.region}</div>
        </div>
      </div>

      {/* Share */}
      <ShareBar signal={s} />

      {/* Summary */}
      {s.summary && (
        <div className="event-card">
          <div className="h">Summary</div>
          <div className="sum">{s.summary}</div>
        </div>
      )}

      {/* Price chart */}
      {s.tickers.length > 0 && (
        <div className="event-card">
          <div className="h">Price Chart</div>
          <StandalonePriceChart signalId={s.id} ticker={s.tickers[0]} eventMs={s.createdAt} base={base} />
        </div>
      )}

      {/* Confidence + Estimated Effect */}
      {(s.confidenceRate != null || s.estimatedEffect) && (
        <div className="event-card">
          <div className="h">Signal Score</div>
          <div className="score-pair">
            {s.confidenceRate != null && (
              <div className="score-box">
                <div className="score-val">{Math.round(s.confidenceRate * 100)}%</div>
                <div className="score-label">Confidence</div>
              </div>
            )}
            {s.estimatedEffect && (
              <div className="score-box">
                <div className="score-val">{s.estimatedEffect.sigma.toFixed(2)}σ</div>
                <div className="score-label">Est. effect · {s.estimatedEffect.pct.toFixed(2)}%</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Relationship tree — master/children, where alert deep-links land */}
      <StandaloneTree s={s} />

      {/* Action */}
      {s.recommendedAction && (
        <div className="event-card">
          <div className="h">Recommended Action</div>
          <div className="action-box"><div className="t">{s.recommendedAction}</div></div>
        </div>
      )}

      {/* Assets */}
      {s.assets.length > 0 && (
        <div className="event-card">
          <div className="h">Affected Assets</div>
          <div className="assets">
            {s.assets.map((a, i) => (
              <div className="asset" key={i}>
                <div>
                  <div className="nm">{a.name || "—"}</div>
                  <div className="kd">{a.kind}{a.country ? ` · ${a.country}` : ""}</div>
                </div>
                <div className="ds">
                  {a.distKm == null ? "—" : a.distKm === 0 ? "on-site" : `${a.distKm.toFixed(1)} km`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <CtaBanner />
    </div>
  );
}

// ── Live Price Panel ────────────────────────────────────────────────────────

function LivePricePanel({ signalId, ticker, eventMs, base }: { signalId: string; ticker: string; eventMs: number; base: string }) {
  const [data, setData] = useState<LivePriceData | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const headers = await authHeaders();
        const r = await fetch(`${base}/signals/${encodeURIComponent(signalId)}/live-price`, { headers });
        if (r.ok) {
          const d = await r.json() as LivePriceData;
          if (alive) setData(d);
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, data?.tracking ? 3000 : 15000);
    return () => { alive = false; clearInterval(id); };
  }, [signalId, base, data?.tracking]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.snaps.length) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const snaps = data.snaps;
    const prices = snaps.map((s) => s.p);
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const pRange = maxP - minP || 1;
    const minT = snaps[0].t;
    const maxT = snaps[snaps.length - 1].t;
    const tRange = maxT - minT || 1;

    const pad = { top: 4, right: 4, bottom: 4, left: 4 };
    const cw = w - pad.left - pad.right;
    const ch = h - pad.top - pad.bottom;
    const x = (t: number) => pad.left + ((t - minT) / tRange) * cw;
    const y = (p: number) => pad.top + ch - ((p - minP) / pRange) * ch;

    // Draw line
    const goingUp = snaps[snaps.length - 1].p >= snaps[0].p;
    ctx.strokeStyle = goingUp ? "#4ade80" : "#f87171";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x(snaps[0].t), y(snaps[0].p));
    for (let i = 1; i < snaps.length; i++) {
      ctx.lineTo(x(snaps[i].t), y(snaps[i].p));
    }
    ctx.stroke();

    // Fill under
    ctx.lineTo(x(snaps[snaps.length - 1].t), h);
    ctx.lineTo(x(snaps[0].t), h);
    ctx.closePath();
    ctx.fillStyle = goingUp ? "rgba(74, 222, 128, 0.08)" : "rgba(248, 113, 113, 0.08)";
    ctx.fill();

    // Event line
    if (eventMs >= minT && eventMs <= maxT) {
      const ex = x(eventMs);
      ctx.strokeStyle = "rgba(255,0,0,0.4)";
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(ex, 0);
      ctx.lineTo(ex, h);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [data, eventMs]);

  useEffect(() => { draw(); }, [draw]);

  if (!data || !data.snaps.length) {
    return <div className="mono-xs" style={{ padding: "12px 0" }}>{data?.tracking ? "Capturing first price…" : "No live price data"}</div>;
  }

  const lastSnap = data.snaps[data.snaps.length - 1];
  const changePct = data.changePct;
  const isUp = changePct !== null && changePct >= 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: 22, fontWeight: 700, color: "var(--ivory)" }}>
          {lastSnap.p.toFixed(2)}
        </span>
        {data.currency && <span className="mono-xs">{data.currency}</span>}
        {changePct !== null && (
          <span style={{
            fontFamily: "var(--mono)",
            fontSize: 18,
            fontWeight: 700,
            color: isUp ? "#4ade80" : "#f87171",
          }}>
            {isUp ? "+" : ""}{changePct.toFixed(2)}%
          </span>
        )}
        {data.tracking && (
          <span className="lifecycle-badge live" style={{ fontSize: 8 }}>TRACKING</span>
        )}
      </div>
      {data.eventPrice && (
        <div className="mono-xs" style={{ marginBottom: 6 }}>
          Event price: {data.eventPrice.toFixed(2)} · {data.snaps.length} snapshots · {ticker}
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: 80, borderRadius: 2, background: "rgba(0,0,0,0.2)" }}
      />
    </div>
  );
}

// ── Mini Map ────────────────────────────────────────────────────────────────

function latLonToPixel(lat: number, lon: number, z: number): [number, number] {
  const n = 2 ** z;
  const px = ((lon + 180) / 360) * n * 256;
  const latR = (lat * Math.PI) / 180;
  const py = (1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2 * n * 256;
  return [px, py];
}

function MiniMap({ lat, lon, title }: { lat: number; lon: number; title: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const Z = 5;
  const W = 500;
  const H = 200;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#0b1310";
    ctx.fillRect(0, 0, W, H);

    const [cx, cy] = latLonToPixel(lat, lon, Z);
    const startPx = cx - W / 2;
    const startPy = cy - H / 2;
    const tileX0 = Math.floor(startPx / 256);
    const tileY0 = Math.floor(startPy / 256);
    const tileX1 = Math.floor((startPx + W) / 256);
    const tileY1 = Math.floor((startPy + H) / 256);
    const n = 2 ** Z;

    let loaded = 0;
    const total = (tileX1 - tileX0 + 1) * (tileY1 - tileY0 + 1);

    for (let tx = tileX0; tx <= tileX1; tx++) {
      for (let ty = tileY0; ty <= tileY1; ty++) {
        if (ty < 0 || ty >= n) continue;
        const wrappedTx = ((tx % n) + n) % n;
        const s = ["a", "b", "c", "d"][(wrappedTx + ty) % 4];
        const url = config.tileUrlTemplate.replace("{s}", s).replace("{z}", String(Z)).replace("{x}", String(wrappedTx)).replace("{y}", String(ty));
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          ctx.drawImage(img, tx * 256 - startPx, ty * 256 - startPy, 256, 256);
          if (++loaded >= total) drawMarker(ctx, W / 2, H / 2);
        };
        img.onerror = () => { if (++loaded >= total) drawMarker(ctx, W / 2, H / 2); };
        img.src = url;
      }
    }
  }, [lat, lon]);

  return (
    <canvas
      ref={canvasRef}
      title={`${title} — ${lat.toFixed(2)}°, ${lon.toFixed(2)}°`}
      style={{ width: "100%", maxWidth: W, height: H, borderRadius: 2, border: "1px solid var(--line)" }}
    />
  );
}

function drawMarker(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  ctx.shadowColor = "rgba(255, 0, 0, 0.5)";
  ctx.shadowBlur = 12;
  ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
  ctx.beginPath(); ctx.arc(x, y, 16, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 6;
  ctx.fillStyle = "rgba(255, 0, 0, 0.4)";
  ctx.beginPath(); ctx.arc(x, y, 8, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "#ff0000"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = "#ff0000";
  ctx.beginPath(); ctx.arc(x, y, 2, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x - 10, y); ctx.lineTo(x + 10, y);
  ctx.moveTo(x, y - 10); ctx.lineTo(x, y + 10);
  ctx.strokeStyle = "rgba(255, 0, 0, 0.5)"; ctx.lineWidth = 1; ctx.stroke();
  ctx.restore();
}

// ── Share Buttons ───────────────────────────────────────────────────────────

function ShareBar({ signal: s }: { signal: Signal }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${s.severity.toUpperCase()}: ${s.title}${s.tickers.length ? ` ($${s.tickers[0]})` : ""} — via @earlywire`;

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const share = (platform: string) => {
    const urls: Record<string, string> = {
      x: `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      reddit: `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(s.title)}`,
      threads: `https://www.threads.net/intent/post?text=${encodeURIComponent(text + " " + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    window.open(urls[platform], "_blank", "noopener,noreferrer,width=600,height=500");
  };

  return (
    <div className="event-share">
      <button className="share-btn" onClick={copyLink} title="Copy link to clipboard">
        {copied ? "✓ COPIED" : "⧉ LINK"}
      </button>
      <button className="share-btn" onClick={() => share("x")} title="Share on X / Twitter">
        𝕏
      </button>
      <button className="share-btn" onClick={() => share("reddit")} title="Share on Reddit">
        REDDIT
      </button>
      <button className="share-btn" onClick={() => share("threads")} title="Share on Threads">
        THREADS
      </button>
      <button className="share-btn" onClick={() => share("facebook")} title="Share on Facebook">
        FB
      </button>
    </div>
  );
}

// ── CTA Banner ──────────────────────────────────────────────────────────────

function CtaBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div className="event-cta">
      <button onClick={() => setDismissed(true)} className="cta-close" title="Dismiss">✕</button>
      <div className="cta-title">Be the first to know</div>
      <div className="cta-body">
        EARLYWIRE detects market-moving events from OSINT sources and delivers
        signals before they hit the wire. This event was detected and classified automatically.
      </div>
      <Link to="/" className="auth-btn primary" style={{ textAlign: "center", fontSize: 11, padding: "10px 14px" }}>
        GET EARLY ACCESS →
      </Link>
    </div>
  );
}

// ── Price Chart (standalone) ────────────────────────────────────────────────

interface ChartBar { t: number; o: number; h: number; l: number; c: number; v: number }
const RANGES = ["4h", "1d", "5d", "1mo"] as const;

function StandalonePriceChart({ signalId, ticker, eventMs, base }: { signalId: string; ticker: string; eventMs: number; base: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [bars, setBars] = useState<ChartBar[] | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [range, setRange] = useState<string>("4h");
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBars(null);
    setErr(false);
    (async () => {
      try {
        const headers = await authHeaders();
        const r = await fetch(
          `${base}/signals/${encodeURIComponent(signalId)}/chart?range=${range}&ticker=${encodeURIComponent(ticker)}`,
          { headers },
        );
        if (!r.ok) throw new Error(String(r.status));
        const d = (await r.json()) as { bars: ChartBar[]; currency: string | null };
        if (!cancelled) { setBars(d.bars); setCurrency(d.currency); }
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [signalId, ticker, range, base]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bars || bars.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const pad = { top: 18, right: 48, bottom: 4, left: 4 };
    const cw = w - pad.left - pad.right;
    const ch = h - pad.top - pad.bottom;
    const minP = Math.min(...bars.map((b) => b.l));
    const maxP = Math.max(...bars.map((b) => b.h));
    const pRange = maxP - minP || 1;
    const minT = bars[0].t;
    const maxT = bars[bars.length - 1].t;
    const tRange = maxT - minT || 1;
    const x = (t: number) => pad.left + ((t - minT) / tRange) * cw;
    const y = (p: number) => pad.top + ch - ((p - minP) / pRange) * ch;

    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const gy = pad.top + (ch * i) / 3;
      ctx.beginPath(); ctx.moveTo(pad.left, gy); ctx.lineTo(w - pad.right, gy); ctx.stroke();
    }
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "9px monospace";
    ctx.textAlign = "left";
    for (let i = 0; i < 4; i++) {
      const pv = maxP - (pRange * i) / 3;
      ctx.fillText(pv.toFixed(2), w - pad.right + 4, pad.top + (ch * i) / 3 + 3);
    }

    const barW = Math.max(1, Math.min(6, (cw / bars.length) * 0.6));
    for (const b of bars) {
      const bx = x(b.t);
      const bull = b.c >= b.o;
      const color = bull ? "#4ade80" : "#f87171";
      ctx.strokeStyle = color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(bx, y(b.h)); ctx.lineTo(bx, y(b.l)); ctx.stroke();
      const bodyTop = y(Math.max(b.o, b.c));
      const bodyBot = y(Math.min(b.o, b.c));
      ctx.fillStyle = color;
      ctx.fillRect(bx - barW / 2, bodyTop, barW, Math.max(1, bodyBot - bodyTop));
    }

    if (eventMs >= minT && eventMs <= maxT) {
      const ex = x(eventMs);
      ctx.save();
      ctx.strokeStyle = "rgba(255, 0, 0, 0.25)"; ctx.lineWidth = 10;
      ctx.beginPath(); ctx.moveTo(ex, 0); ctx.lineTo(ex, h); ctx.stroke();
      ctx.strokeStyle = "#ff0000"; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(ex, 0); ctx.lineTo(ex, h); ctx.stroke();
      ctx.fillStyle = "#ff0000"; ctx.font = "bold 9px monospace"; ctx.textAlign = "center";
      ctx.fillText("▼ EVENT", ex, pad.top - 6);
      ctx.restore();
    }

    const firstC = bars[0].c;
    const lastC = bars[bars.length - 1].c;
    const pctChg = ((lastC - firstC) / firstC) * 100;
    const sign = pctChg >= 0 ? "+" : "";
    ctx.fillStyle = pctChg >= 0 ? "#4ade80" : "#f87171";
    ctx.font = "bold 10px monospace"; ctx.textAlign = "right";
    ctx.fillText(`${sign}${pctChg.toFixed(2)}%`, w - pad.right, pad.top - 6);
  }, [bars, eventMs]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span className="mono-xs">{ticker}{currency ? ` · ${currency}` : ""}</span>
        <div style={{ display: "flex", gap: 2 }}>
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)} className="mono-xs" title={`Show ${r} price range`}
              style={{
                background: range === r ? "rgba(var(--green-rgb), 0.15)" : "transparent",
                color: range === r ? "var(--green)" : "var(--ivory-d)",
                border: "none", padding: "2px 6px", cursor: "pointer",
              }}
            >{r}</button>
          ))}
        </div>
      </div>
      {err && <div className="mono-xs" style={{ color: "var(--red)" }}>chart unavailable</div>}
      {!bars && !err && <div className="mono-xs">loading chart…</div>}
      {bars && bars.length === 0 && <div className="mono-xs">no bars (market closed?)</div>}
      {bars && bars.length > 0 && (
        <canvas ref={canvasRef} style={{ width: "100%", height: 160, background: "var(--bg-2)", borderRadius: 2 }} />
      )}
    </div>
  );
}

/** Situation tree on the standalone page: signal navigation swaps the route (the ops screen
 *  isn't mounted here); raw event nodes render as ids since there's no live event store. */
function StandaloneTree({ s }: { s: Signal }) {
  const nav = useNavigate();
  return (
    <SituationTree
      s={s}
      events={[]}
      onSelectSignal={(id) => nav(`/signal/${encodeURIComponent(id)}`)}
      onSelectEvent={() => {}}
    />
  );
}
